import * as React from "react";
import {
  useAuth,
  useChatEmbed,
  createChatHistoryStore,
  createLocalFolderProvider,
  createGraphAuth,
  type LocalFolderProvider,
  type ChatHistoryStore,
  type ParentToolSchema,
  type ToolCallCapabilities,
  type HostContextProvider,
  type GeneratedImagePayload,
  type GraphAuth,
} from "@usable/office-embed";
import { GraphConnect } from "./components/GraphConnect";
import { HistoryPanel } from "./components/HistoryPanel";
import { OFFICE_ACCENT } from "./theme";
import { DevInspector, persistInspectorOpen, readInspectorOpen } from "./components/DevInspector";
import { pushDebugLog } from "./debug-log";
import defaultLogoUrl from "@usable/office-assets/logo.png";

// ---------------------------------------------------------------------------
// App config — the "thin app" contract
// ---------------------------------------------------------------------------

export interface AppConfig {
  /** Display name on the sign-in screen, e.g. "Excel Assistant". */
  appName: string;
  /** The document noun for the sign-in copy, e.g. "workbook" | "document" | "presentation" | "email". */
  documentNoun: string;
  /** Public embed token selecting the Usable workspace/expert. */
  embedToken: string;
  /** Host-specific Office.js tool schemas. */
  toolSchemas: ParentToolSchema[];
  /**
   * Host-specific tool-call handler. The third argument exposes opt-in host
   * capabilities (the embed's `addImage`, the requestId); most apps take just
   * `(tool, args)` and ignore it — PowerPoint uses it to deliver slide
   * screenshots as chat attachments.
   */
  onToolCall: (tool: string, args: unknown, caps: ToolCallCapabilities) => Promise<unknown>;
  /** IndexedDB database name for local chat history, e.g. "usable-excel-chat-history". */
  dbName: string;
  /**
   * Opt-in Microsoft Graph capability (Outlook only). When present, the shell
   * builds a NAA token provider, injects `acquireGraphToken` into every tool
   * call's `caps`, and renders the "Connect Microsoft 365" settings affordance.
   * `authority` MUST be tenant-specific for a single-tenant app reg
   * (`https://login.microsoftonline.com/<tenantId>`) — `/common` errors AADSTS50194.
   * `scopes` is the UNION consented at connect-time (e.g. Mail.Read + Calendars.Read)
   * so the user consents once. Omit the whole block to leave Graph off.
   */
  graph?: { clientId: string; authority: string; scopes: string[] };
  /** Optional logo URL override. Defaults to the shared Usable brand logo from
   *  @usable/office-assets; an app passes its own only for a host-specific logo. */
  logoUrl?: string;
  /** Expected iframe origin. Defaults to https://chat.usable.dev. */
  iframeOrigin?: string;
  /**
   * Optional host-specific reader of the current document/selection/email,
   * injected into the embed as ambient context so the assistant knows what
   * "this" refers to. Lives in each app (Office.js); the shell just forwards it.
   */
  contextProvider?: HostContextProvider;
  /**
   * Optional handler for AI-generated images pushed from the embed — hosts
   * cache the bytes so their document tools can insert "the image you just
   * generated" without any cross-origin fetch (opt-in seam, like addImage).
   */
  onImageGenerated?: (payload: GeneratedImagePayload) => void;
}

// ---------------------------------------------------------------------------
// ChatPane — only mounted when authenticated
// ---------------------------------------------------------------------------

interface ChatPaneProps {
  config: AppConfig;
  store: ChatHistoryStore;
  accessToken: string;
  ensureValidToken: () => Promise<string | null>;
}

function ChatPane({ config, store, accessToken, ensureValidToken }: ChatPaneProps): React.ReactElement {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  // Parent-served "Local Folder" knowledge source (host-agnostic — every app
  // gets it): the task pane picks a folder (webkitdirectory works in WKWebView
  // AND Chromium, unlike the FS-Access pickers the iframe's own Local Folder
  // needs), holds a read-only snapshot, and serves local_folder_* parent tools.
  const folderProviderRef = React.useRef<LocalFolderProvider>();
  if (!folderProviderRef.current) {
    folderProviderRef.current = createLocalFolderProvider();
  }
  const folderProvider = folderProviderRef.current;
  const folderInputRef = React.useRef<HTMLInputElement>(null);
  const [localFolder, setLocalFolder] = React.useState<{ name: string; fileCount: number } | null>(null);

  // One NAA token provider for the app's lifetime (Outlook only). Shared between
  // the tool-call path (via caps.acquireGraphToken) and the Connect Microsoft 365
  // menu affordance, so a consent granted in the menu is immediately usable by tools.
  const graphAuth = React.useMemo<GraphAuth | undefined>(
    () =>
      config.graph
        ? createGraphAuth({ clientId: config.graph.clientId, authority: config.graph.authority })
        : undefined,
    [config.graph]
  );

  const chatApi = useChatEmbed(iframeRef, accessToken, ensureValidToken, {
    embedToken: config.embedToken,
    toolSchemas: config.toolSchemas,
    // Folder tools are intercepted in the shell; everything else routes to the
    // app's host tools unchanged.
    onToolCall: (tool, args, caps) =>
      folderProvider.canHandle(tool)
        ? folderProvider.handleToolCall(tool, (args ?? {}) as Record<string, unknown>)
        : config.onToolCall(tool, args, caps),
    graphAuth,
    historyStore: store,
    iframeOrigin: config.iframeOrigin,
    contextProvider: config.contextProvider,
    onImageGenerated: config.onImageGenerated,
    onMessage: (type, payload) => pushDebugLog(type, payload),
  });

  const attachFolder = React.useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  const onFolderPicked = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      const { folderName, fileCount } = folderProvider.attach(files);
      setLocalFolder({ name: folderName, fileCount });
      chatApi.registerExtraTools(folderProvider.toolSchemas);
      chatApi.setContextEntry("attached_local_folder", folderProvider.summary());
      pushDebugLog("local-folder→attach", { folderName, fileCount });
      // Allow re-picking the same folder (change events need a value reset).
      event.target.value = "";
    },
    [chatApi, folderProvider]
  );

  const detachFolder = React.useCallback(() => {
    folderProvider.detach();
    setLocalFolder(null);
    chatApi.deregisterExtraTools(folderProvider.toolSchemas.map((t) => t.name));
    chatApi.setContextEntry("attached_local_folder", null);
    pushDebugLog("local-folder→detach", {});
  }, [chatApi, folderProvider]);

  // Dev inspector open-state lives here so the history-drawer footer button drives
  // the same (persisted) state the inspector renders from.
  const [inspectorOpen, setInspectorOpen] = React.useState(readInspectorOpen);
  const toggleInspector = React.useCallback(() => {
    setInspectorOpen((prev) => {
      const next = !prev;
      persistInspectorOpen(next);
      return next;
    });
  }, []);

  return (
    <>
      <iframe
        ref={iframeRef}
        src="about:blank"
        title="Usable Chat"
        style={styles.iframe}
        allow="clipboard-read; clipboard-write"
      />
      {/* Hidden directory picker. webkitdirectory is non-standard but works in
          WebKit + Chromium; where a host ignores it, the same input degrades to
          multi-file selection (flat paths — the provider handles both). */}
      <input
        ref={folderInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={onFolderPicked}
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
      />
      <HistoryPanel
        api={chatApi}
        store={store}
        onToggleInspector={toggleInspector}
        localFolder={localFolder}
        onAttachFolder={attachFolder}
        onDetachFolder={detachFolder}
        graphConnect={
          graphAuth && config.graph ? (
            <GraphConnect graphAuth={graphAuth} scopes={config.graph.scopes} />
          ) : undefined
        }
      />
      <DevInspector open={inspectorOpen} onClose={() => toggleInspector()} />
    </>
  );
}

// ---------------------------------------------------------------------------
// App — auth state machine, driven by config
// ---------------------------------------------------------------------------

export function App({ config }: { config: AppConfig }): React.ReactElement {
  const { state, accessToken, login, ensureValidToken } = useAuth();
  // One store per app, keyed by its IndexedDB name.
  const store = React.useMemo<ChatHistoryStore>(
    () => createChatHistoryStore({ dbName: config.dbName }),
    [config.dbName]
  );

  if (state === "restoring") {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.label}>Signing in…</p>
      </div>
    );
  }

  if (state === "unauthenticated") {
    // Apps don't ship a logo; the shared brand logo is the default, overridable per-app.
    const logoUrl = config.logoUrl ?? defaultLogoUrl;
    return (
      <div style={styles.center}>
        <img src={logoUrl} alt="Usable" style={styles.logo} />
        <p style={styles.heading}>{config.appName}</p>
        <p style={styles.label}>Sign in to start chatting with your {config.documentNoun}.</p>
        <button onClick={login} style={styles.button}>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <ChatPane
      config={config}
      store={store}
      accessToken={accessToken!}
      ensureValidToken={ensureValidToken}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = {
  center: {
    display: "flex" as const,
    flexDirection: "column" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    height: "100vh",
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    gap: 8,
    padding: "0 24px",
    boxSizing: "border-box" as const,
    textAlign: "center" as const,
    background: "Canvas",
    color: "CanvasText",
  },
  logo: { width: 96, height: 96, marginBottom: 8 },
  spinner: {
    width: 28,
    height: 28,
    border: "3px solid rgba(128,128,128,0.35)",
    borderTopColor: OFFICE_ACCENT,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginBottom: 8,
  },
  heading: { margin: 0, fontSize: 16, fontWeight: 600 as const, color: "CanvasText" },
  label: { margin: 0, fontSize: 13, color: "GrayText" },
  button: {
    marginTop: 12,
    padding: "8px 24px",
    background: OFFICE_ACCENT,
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer" as const,
    fontSize: 14,
    fontFamily: "inherit",
  },
  iframe: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
  },
} as const;
